import { Button } from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { Check, ChevronsUpDown } from 'lucide-react'
import * as React from 'react'

interface ComboboxProps {
	items: { value: string; label: string }[]
	placeholder?: string
	value?: string
	onValueChange?: (value: string) => void
	disabled?: boolean
	isLoading?: boolean
	loadingMessage?: string
}

export function Combobox({
	items,
	placeholder = 'Select...',
	value,
	onValueChange,
	disabled,
	isLoading,
	loadingMessage = 'Loading...',
}: ComboboxProps) {
	const [open, setOpen] = React.useState(false)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className="w-full justify-between cursor-pointer"
					disabled={disabled || isLoading}
				>
					<span className="truncate">
						{isLoading
							? loadingMessage
							: value
								? items.find((item) => item.value === value)?.label
								: placeholder}
					</span>
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[--radix-popover-trigger-width] p-0"
				align="start"
				sideOffset={4}
			>
				<Command>
					<CommandInput placeholder="Search..." />
					<CommandList>
						{isLoading ? (
							<div className="p-2 text-center text-sm">{loadingMessage}</div>
						) : (
							<>
								<CommandEmpty>No items found.</CommandEmpty>
								<CommandGroup>
									{items.map((item) => (
										<CommandItem
											key={item.value}
											value={item.label}
											className="cursor-pointer"
											onSelect={(currentValue) => {
												const selectedItem = items.find(
													(i) => i.label.toLowerCase() === currentValue.toLowerCase()
												)
												if (selectedItem) {
													onValueChange?.(selectedItem.value === value ? '' : selectedItem.value)
												}
												setOpen(false)
											}}
										>
											{item.label}
											<Check
												className={cn(
													'ml-auto h-4 w-4',
													value === item.value ? 'opacity-100' : 'opacity-0'
												)}
											/>
										</CommandItem>
									))}
								</CommandGroup>
							</>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
